/**
 * Helpers de dates pour les vues mobiles.
 *
 * Tout est manipulé en UTC strict pour rester cohérent avec les colonnes
 * `@db.Date` côté backend (pas de décalage de fuseau qui ferait basculer un
 * jour). Une date "jour" = un Date à minuit UTC.
 */

// "YYYY-MM-DD" depuis un Date (en UTC).
export function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

// Parse "YYYY-MM-DD" en Date minuit UTC.
export function parseIso(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

// Ajoute n jours (UTC).
export function addDays(date, n) {
  const d = new Date(date.getTime());
  d.setUTCDate(d.getUTCDate() + n);
  return d;
}

// Lundi (00:00 UTC) de la semaine contenant `date`.
export function mondayOf(date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay(); // 0 = dimanche
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(d, diff);
}

// Aujourd'hui à minuit UTC.
export function todayUtc() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

const JOURS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const JOURS_COURT = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const MOIS = [
  'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
];

export function dayLabel(date) {
  return JOURS[date.getUTCDay()];
}

export function dayLabelShort(date) {
  return JOURS_COURT[date.getUTCDay()];
}

// "8 juin" (sans année).
export function dayMonthLabel(date) {
  return `${date.getUTCDate()} ${MOIS[date.getUTCMonth()]}`;
}

// "Lun 8 juin"
export function fullDayLabel(date) {
  return `${dayLabelShort(date)} ${date.getUTCDate()} ${MOIS[date.getUTCMonth()]}`;
}

// "2 – 8 juin 2026" (plage de semaine).
export function weekRangeLabel(monday) {
  const sunday = addDays(monday, 6);
  const sameMonth = monday.getUTCMonth() === sunday.getUTCMonth();
  const left = sameMonth
    ? String(monday.getUTCDate())
    : `${monday.getUTCDate()} ${MOIS[monday.getUTCMonth()]}`;
  const right = `${sunday.getUTCDate()} ${MOIS[sunday.getUTCMonth()]} ${sunday.getUTCFullYear()}`;
  return `${left} – ${right}`;
}

// true si le jour `date` est un jour ouvré (lun→ven).
export function isWeekday(date) {
  const day = date.getUTCDay();
  return day >= 1 && day <= 5;
}

// 7 dates du lundi au dimanche.
export function weekDays(monday) {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

// Durcissement défensif (F-MOB-06) : ne laisse passer qu'une couleur hex
// valide (#RGB, #RRGGBB, #RRGGBBAA) avant injection dans un style inline.
// Toute valeur malformée (ou absente) retombe sur le fallback fourni. Évite
// qu'une `affaire.color` / `timeOffType.colorHex` corrompue casse l'affichage.
// `Affaire.color` est parfois stocké SANS `#` (import Float) : on le rajoute.
const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
export function normalizeHex(value) {
  if (typeof value !== 'string') return null;
  let v = value.trim();
  if (v && v[0] !== '#') v = `#${v}`;
  return HEX_RE.test(v) ? v : null;
}
export function safeColor(value, fallback = 'var(--accent)') {
  return normalizeHex(value) ?? fallback;
}

// Variante translucide (~13% d'opacité, suffixe `22`) pour les fonds de bloc.
// Le suffixe alpha n'est valable que sur une forme #RRGGBB.
export function safeColorAlpha(value, fallback = 'var(--glass)') {
  const hex = normalizeHex(value);
  return hex && hex.length === 7 ? `${hex}22` : fallback;
}

// ---- Vue calendrier mensuelle (Lot 3 — ergonomie) ----

const MOIS_LONG = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

// 1er du mois (00:00 UTC) contenant `date`.
export function firstOfMonth(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

// 1er du mois décalé de n mois.
export function addMonths(date, n) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + n, 1));
}

// "Septembre 2026".
export function monthLabel(date) {
  const m = MOIS_LONG[date.getUTCMonth()];
  return `${m.charAt(0).toUpperCase()}${m.slice(1)} ${date.getUTCFullYear()}`;
}

// Jours de la grille mensuelle, lundi → dimanche, semaines complètes :
// 28 à 42 jours (4 à 6 semaines).
export function monthGridDays(monthStart) {
  const start = mondayOf(firstOfMonth(monthStart));
  const lastDay = new Date(
    Date.UTC(monthStart.getUTCFullYear(), monthStart.getUTCMonth() + 1, 0),
  );
  const end = addDays(mondayOf(lastDay), 6);
  const days = [];
  for (let d = start; d.getTime() <= end.getTime(); d = addDays(d, 1)) days.push(d);
  return days;
}

/**
 * Jours travaillés, indexés par `getUTCDay()` (0 = dimanche). Même règle que
 * `getWorkingDayMask` du Schedule : `workDaysHours` (objet ou tableau de 7)
 * si renseigné avec au moins un jour, sinon lundi → vendredi.
 */
export function workingDayMask(workDaysHours) {
  const DEFAULT_5J = [false, true, true, true, true, true, false];
  if (!workDaysHours || typeof workDaysHours !== 'object') return DEFAULT_5J;
  const keys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const values = Array.isArray(workDaysHours) ? workDaysHours : keys.map((k) => workDaysHours[k]);
  if (values.length !== 7) return DEFAULT_5J;
  const mask = values.map((v) => Number.isFinite(Number(v)) && Number(v) > 0);
  return mask.some(Boolean) ? mask : DEFAULT_5J;
}
